import axios from 'axios';

declare global {
  interface Window {
    __AVUKASA_API_BASE_URL__?: string;
  }
}

const resolveRuntimeOverride = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  if (window.__AVUKASA_API_BASE_URL__) {
    return window.__AVUKASA_API_BASE_URL__;
  }

  try {
    const stored = window.localStorage.getItem('avukasa_api_base_url');
    if (stored) {
      return stored;
    }
  } catch {
    // localStorage erişilemiyorsa yoksay
  }

  return undefined;
};

const resolveDefaultBaseUrl = () => {
  if (process.env.REACT_APP_DEFAULT_API_URL) {
    return process.env.REACT_APP_DEFAULT_API_URL;
  }

  // Production modunda sadece production API URL'ini kullan
  if (process.env.NODE_ENV === 'production') {
    return 'https://api.avukatech.com/api/v1';
  }

  // Development modunda localhost kontrolü
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8080/api/v1';
    }
    if (hostname.endsWith('vercel.app') || hostname.endsWith('avukatech.com')) {
      return 'https://api.avukatech.com/api/v1';
    }
  }

  // Development için varsayılan localhost
  return 'http://localhost:8080/api/v1';
};

const API_BASE_URL =
  process.env.REACT_APP_API_URL || resolveRuntimeOverride() || resolveDefaultBaseUrl();

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'X-Requested-With': 'XMLHttpRequest', // CSRF koruması için
  },
  withCredentials: true, // Cookie'ler için
  xsrfCookieName: 'XSRF-TOKEN', // Backend'deki CSRFCookieName ile aynı olmalı
  xsrfHeaderName: 'X-XSRF-TOKEN', // Backend'deki CSRFHeaderName ile aynı olmalı
});

// Request interceptor - her istekte token ve CSRF token kontrolü
apiClient.interceptors.request.use(
  (config) => {
    // Auth token'ı her istekte kontrol et
    const token = localStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // CSRF token'ı cookie'den oku ve header'a ek
    // Backend Double Submit Cookie pattern kullanıyor
    if (typeof document !== 'undefined') {
      const csrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (csrfToken && config.headers) {
        config.headers['X-XSRF-TOKEN'] = csrfToken;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - hata yönetimi
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 401 Unauthorized - token geçersiz veya süresi dolmuş
    if (error.response?.status === 401) {
      // Token'ı temizle
      localStorage.removeItem('authToken');
      delete apiClient.defaults.headers.common.Authorization;

      // Login sayfasına yönlendir (ancak bazı sayfalarda yönlendirme yapma)
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login') &&
        !currentPath.includes('/signup') &&
        !currentPath.includes('/forgot-password') &&
        !currentPath.includes('/reset-password') &&
        !currentPath.includes('/auth/verify-email') &&
        !currentPath.includes('/onboarding')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const setAuthToken = (token: string | null) => {
  if (token) {
    apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common.Authorization;
  }
};

export default apiClient;

